import { classNames } from '@schuelerhilfe/shared/src/utils/classNames';
import type { ReactNode } from 'react';
import type { HeroCardRootProps } from './types';

export type HeroCardAlign = 'left' | 'center';

/**
 * `gradient` is the white→cream panel that floats over the hero image.
 * `solid` is flat cream, for the cards whose Hero paints the same colour
 * behind them so panel and page read as one band (see `HeroComponent`'s
 * full-width branch).
 */
export type HeroCardSurface = 'gradient' | 'solid';

interface HeroCardPanelProps extends HeroCardRootProps {
  children: ReactNode;
  align?: HeroCardAlign;
  surface?: HeroCardSurface;
  /** Dropped when the card renders its own tab strip above the panel. */
  roundedTop?: boolean;
}

/**
 * The white→cream panel every Hero Card shares, reusing the exact gradient
 * and radius `LocationCard` already renders in this slot so the two cannot
 * drift apart.
 *
 * `align` centers the card's own blocks. It deliberately does not set
 * `text-align` on the whole subtree: a centered card still keeps its bullet
 * list left-aligned as a block that is itself centered, so the checkmarks line
 * up in one column (SH-312 AC 3).
 */
export function HeroCardPanel({
  children,
  align = 'left',
  surface = 'gradient',
  className,
  roundedTop = true,
  ...rest
}: HeroCardPanelProps) {
  return (
    <div
      {...rest}
      className={classNames(
        // `.section-fade` (cream at the bottom, transparent at the top) over a
        // white panel reproduces the legacy `.tab-content-gradient`
        // (`#fff` → `#faf5eb`) at every breakpoint. Deliberately NOT the
        // `section-fade-bottom md:section-fade` pair the Location Card uses:
        // both are hand-written CSS classes rather than Tailwind utilities, so
        // Tailwind never emits the `md:` variant and only the inverted
        // `-bottom` direction would ever apply. See the note in globals.css.
        'text-foreground flex w-full flex-col gap-4 p-6',
        surface === 'solid' ? 'bg-secondary-lighter' : 'section-fade bg-white',
        roundedTop && 'rounded-t-2xl',
        align === 'center' ? 'items-center text-center' : 'items-stretch',
        className,
      )}
    >
      {children}
    </div>
  );
}
